import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, Wrench } from "lucide-react";

const fmt = (v) => (typeof v === "number" ? formatIDR(v) : (v ?? "-"));

export const ReconcileDialog = ({ open, onOpenChange, onFixed }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => { api.get("/bills/reconcile").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); };
  useEffect(() => { if (open) { setData(null); load(); } }, [open]);

  const fix = async (i) => {
    setBusy(i.bill_id + i.kind);
    try {
      await api.post("/bills/reconcile/fix", { bill_id: i.bill_id, kind: i.kind });
      toast.success(`${i.invoice_no} diperbaiki & tercatat di audit log`);
      load();
      onFixed();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Rekonsiliasi Tagihan Vendor</DialogTitle>
          <DialogDescription>Perbandingan data tagihan, pembayaran & order di database. Tidak ada yang diubah sebelum Anda menekan Perbaiki.</DialogDescription>
        </DialogHeader>
        {!data && <p className="text-sm text-muted-foreground py-6 text-center">Memeriksa database...</p>}
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="reconcile-totals">
              {[["Diperiksa", `${data.checked} tagihan`], ["Total Tagihan", formatIDR(data.totals.tagihan)], ["Total Dibayar", formatIDR(data.totals.dibayar)], ["Sisa Hutang", formatIDR(data.totals.sisa)]].map(([k, v]) => (
                <div key={k} className="rounded-lg border p-2.5"><p className="text-muted-foreground">{k}</p><p className="font-semibold text-sm">{v}</p></div>
              ))}
            </div>
            {data.issues.length === 0 ? (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 p-4 text-sm text-emerald-700" data-testid="reconcile-clean">
                <CheckCircle2 className="h-4 w-4" /> Tidak ada selisih. Semua tagihan sesuai dengan pembayaran dan order sumber.
              </div>
            ) : (
              <div className="border rounded-lg divide-y" data-testid="reconcile-issues">
                {data.issues.map((i) => (
                  <div key={i.bill_id + i.kind} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 text-sm" data-testid={`reconcile-issue-${i.kind}-${i.bill_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{i.invoice_no} · {i.supplier_name} <Badge variant="outline" className="ml-1 text-[10px]">{i.admin}</Badge></p>
                      <p className="text-xs text-rose-600">{i.label}</p>
                      <p className="text-xs text-muted-foreground">Sebelum: {fmt(i.before)} → Seharusnya: {fmt(i.after)} · Tagihan {formatIDR(i.amount)} · Dibayar {formatIDR(i.paid)}</p>
                    </div>
                    {i.fixable ? (
                      <Button size="sm" variant="gold" className="gap-1 h-8 text-xs shrink-0" disabled={busy === i.bill_id + i.kind} onClick={() => fix(i)} data-testid={`reconcile-fix-${i.kind}-${i.bill_id}`}>
                        <Wrench className="h-3.5 w-3.5" /> Perbaiki
                      </Button>
                    ) : <span className="text-[11px] text-muted-foreground shrink-0">Periksa manual</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
